import React, { useEffect, useState, useRef } from 'react';
import { Modal, Radio, Button, message, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { getPaymentCheckoutToken, addPatientPaymentMethod } from 'api/provider';
import { SQUARE_SCRIPT_URL } from 'constants/square';
import s from './s.module.less';

declare global {
    interface Window {
        Square?: {
            payments: (applicationId: string, locationId: string) => {
                card: () => Promise<{
                    attach: (selector: string) => Promise<void>;
                    tokenize: (opts?: { intent?: string }) => Promise<{ status: string; token?: string; errors?: unknown }>;
                    destroy: () => Promise<void>;
                }>;
            };
        };
    }
}

export interface IChargeCardItem {
    id: string;
    brand: string;
    last4: string;
    expMonth: string;
    expYear: string;
    paymentMethodId?: string;
}

interface ChargeCardModalProps {
    open: boolean;
    onCancel: () => void;
    onChargeCard: (paymentMethodId?: string, paymentToken?: string) => void | Promise<void>;
    /** 本次扣款金额（美元），用于展示 */
    amount?: number;
    /** 患者已有卡片列表（由父组件拉取 getPatientPaymentMethodList 后传入） */
    cards?: IChargeCardItem[];
    /** 卡片列表加载中（如拉取支付方式列表时） */
    loading?: boolean;
    /** 患者 clientId，添加新卡时必传，用于调用 addPatientPaymentMethod */
    clientId?: number;
    /** 添加新卡成功后回调，父组件可在此 refetch 支付方式列表 */
    onCardAdded?: () => void;
}

const formatExp = (expMonth: string, expYear: string) => {
    const month = expMonth?.padStart(2, '0') || '00';
    const year = expYear?.length === 2 ? expYear : (expYear || '').slice(-2);
    return `${month}/${year}`;
};

/** 动态加载 Square 脚本，返回 Promise<boolean> 表示是否加载成功 */
const loadSquareScript = (): Promise<boolean> => {
    if (typeof window === 'undefined') return Promise.resolve(false);
    if (window.Square) return Promise.resolve(true);
    const existing = document.querySelector(`script[src="${SQUARE_SCRIPT_URL}"]`);
    if (existing) return Promise.resolve(!!window.Square);
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = SQUARE_SCRIPT_URL;
        script.async = true;
        script.onload = () => resolve(!!window.Square);
        script.onerror = () => resolve(false);
        document.head.appendChild(script);
    });
};

const ChargeCardModal: React.FC<ChargeCardModalProps> = ({
    open,
    onCancel,
    onChargeCard,
    amount = 120,
    cards = [],
    loading: cardsLoading = false,
    clientId,
    onCardAdded,
}) => {
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const [showAddCardForm, setShowAddCardForm] = useState(false);
    const [saveCardLoading, setSaveCardLoading] = useState(false);
    const [chargeLoading, setChargeLoading] = useState(false);
    /** Square 初始化中（拉 token + 加载脚本 + 挂载卡表单） */
    const [squareInitLoading, setSquareInitLoading] = useState(false);
    /** Square 初始化失败原因，有值则展示错误 */
    const [squareInitError, setSquareInitError] = useState<string | null>(null);
    /** 当前弹窗内可见的卡片列表（含新添加的，新卡在顶部） */
    const [localCards, setLocalCards] = useState<IChargeCardItem[]>([]);
    const squareCardRef = useRef<{ tokenize: () => Promise<{ status: string; token?: string }>; destroy: () => Promise<void> } | null>(null);
    const squareCardContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            setLocalCards([...cards]);
            setSelectedCardId(cards.length > 0 ? cards[0].id : null);
            setShowAddCardForm(false);
            setSquareInitError(null);
        }
    }, [open, cards]);

    const hasMultipleCards = localCards.length > 1;
    const hasAnyCard = localCards.length > 0;
    const displayAmount = typeof amount === 'number' ? amount.toFixed(2) : '0.00';
    const canCharge = !!selectedCardId;

    const handleAddNewCardClick = () => {
        setShowAddCardForm(true);
        setSquareInitError(null);
    };

    /** 展示「添加新卡」时：拉取 checkout token、加载 Square 脚本并挂载卡表单 */
    useEffect(() => {
        if (!showAddCardForm || !open) return;

        let cancelled = false;
        setSquareInitLoading(true);
        setSquareInitError(null);
        getPaymentCheckoutToken()
            .then((res) => {
                if (cancelled) return;
                const data = res?.data?.data;
                const applicationId = data?.squareApplicationId;
                const locationId = data?.squareLocationId;
                if (!applicationId || !locationId) {
                    setSquareInitError('Payment is not configured. Missing Square credentials.');
                    setSquareInitLoading(false);
                    return;
                }
                return loadSquareScript().then((loaded) => ({ loaded, applicationId, locationId }));
            })
            .then((result) => {
                if (cancelled || !result) return;
                const { loaded, applicationId, locationId } = result;
                if (!loaded || !window.Square) {
                    setSquareInitError('Failed to load payment form.');
                    setSquareInitLoading(false);
                    return;
                }
                const payments = window.Square.payments(applicationId, locationId);
                return payments.card().then((card) => {
                    if (cancelled) return card.destroy();
                    squareCardRef.current = card;
                    return card.attach('#square-card-container').then(() => {
                        if (!cancelled) setSquareInitLoading(false);
                    });
                });
            })
            .catch((err) => {
                if (!cancelled) {
                    setSquareInitError(err?.message || 'Failed to initialize payment form.');
                    setSquareInitLoading(false);
                }
            });
        return () => {
            cancelled = true;
            if (squareCardRef.current) {
                squareCardRef.current.destroy().catch(() => {});
                squareCardRef.current = null;
            }
        };
    }, [showAddCardForm, open]);

    const handleSaveCard = async () => {
        if (!squareCardRef.current || clientId == null) {
            if (clientId == null) message.error('Patient is required to add a card.');
            return;
        }
        setSaveCardLoading(true);
        try {
            const tokenResult = await squareCardRef.current.tokenize();
            if (tokenResult.status !== 'OK' || !tokenResult.token) {
                message.error('Invalid card. Please check and try again.');
                return;
            }
            const res = await addPatientPaymentMethod({
                clientId,
                paymentToken: tokenResult.token,
                paymentChannel: 'SQUARE',
                paymentType: 'CREDIT_CARD',
            });
            if (res?.error || res?.data?.code !== 200) {
                message.error(res?.data?.message || 'Failed to save card.');
                return;
            }
            message.success('Card saved. It will be synced to Insurance & payments.');
            setShowAddCardForm(false);
            onCardAdded?.();
            setSquareInitError(null);
        } catch (e) {
            message.error((e as Error)?.message || 'Failed to save card');
        } finally {
            setSaveCardLoading(false);
        }
    };

    const handleCancelAddCard = () => {
        if (squareCardRef.current) {
            squareCardRef.current.destroy().catch(() => {});
            squareCardRef.current = null;
        }
        setShowAddCardForm(false);
        setSquareInitError(null);
    };

    const handleChargeNow = async () => {
        if (!canCharge) return;
        const card = localCards.find((c) => c.id === selectedCardId);
        setChargeLoading(true);
        try {
            const pmId = card?.paymentMethodId ?? undefined;
            await Promise.resolve(onChargeCard(pmId, undefined));
        } finally {
            setChargeLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            width={650}
            centered
            className={s.chargeCardModal}
            rootClassName={s.chargeCardModalRoot}
            wrapClassName={s.chargeCardModalWrap}
            closeIcon={<CloseOutlined className={s.closeIcon} />}
            destroyOnClose
            zIndex={10000}
            getContainer={() => document.body}
        >
            <div className={s.modalContent}>
                <div className={s.modalHeader}>
                    <h2 className={s.modalTitle}>Charge card</h2>
                </div>

                <div className={s.amountRow}>
                    Amount: <span className={s.amountValue}>${displayAmount}</span>
                </div>

                {cardsLoading ? (
                    <div className={s.loadingWrap}>
                        <Spin tip="Loading payment methods..." />
                    </div>
                ) : (
                <>
                {/* 选卡区域：多卡为 Radio 列表，单卡为单个展示，无卡仅显示「Add a new card」 */}
                <div>
                    {hasMultipleCards && <h3 className={s.sectionTitle}>Select a card to charge</h3>}
                    {hasMultipleCards && (
                        <Radio.Group
                            value={selectedCardId ?? undefined}
                            onChange={(e) => setSelectedCardId(e.target.value)}
                            style={{ width: '100%' }}
                        >
                            {localCards.map((card) => (
                                <div
                                    key={card.id}
                                    className={`${s.cardItem} ${selectedCardId === card.id ? s.selected : ''}`}
                                    onClick={() => setSelectedCardId(card.id)}
                                >
                                    <Radio value={card.id} />
                                    <div className={s.cardInfo}>
                                        <div className={s.cardLabel}>
                                            {card.brand}...{card.last4}
                                        </div>
                                        <div className={s.cardExp}>
                                            Exp. {formatExp(card.expMonth, card.expYear)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Radio.Group>
                    )}
                    {localCards.length === 1 && !showAddCardForm && (
                        <div className={`${s.cardItem} ${s.selected}`}>
                            <div className={s.cardInfo}>
                                <div className={s.cardLabel}>
                                    {localCards[0].brand}...{localCards[0].last4}
                                </div>
                                <div className={s.cardExp}>
                                    Exp. {formatExp(localCards[0].expMonth, localCards[0].expYear)}
                                </div>
                            </div>
                        </div>
                    )}
                    {hasAnyCard && !showAddCardForm && (
                        <span role="button" tabIndex={0} className={s.addNewCardLink} onClick={handleAddNewCardClick} onKeyDown={(e) => e.key === 'Enter' && handleAddNewCardClick()}>
                            Add a new card
                        </span>
                    )}
                    {!hasAnyCard && !showAddCardForm && (
                        <span role="button" tabIndex={0} className={s.addNewCardLink} onClick={handleAddNewCardClick} onKeyDown={(e) => e.key === 'Enter' && handleAddNewCardClick()}>
                            Add a new card
                        </span>
                    )}
                </div>

                {/* 添加新卡：使用 Square Web Payments SDK 信用卡表单，由 getPaymentCheckoutToken 初始化，保存时调用 addPatientPaymentMethod */}
                {showAddCardForm && (
                    <div className={s.addCardSection}>
                        <h3 className={s.sectionTitle}>Add a new card</h3>
                        {squareInitError && (
                            <div className={s.squareError}>{squareInitError}</div>
                        )}
                        {squareInitLoading && (
                            <div className={s.squareLoading}>
                                <Spin size="small" /> Loading payment form...
                            </div>
                        )}
                        <div
                            ref={squareCardContainerRef}
                            id="square-card-container"
                            className={s.squareCardContainer}
                            style={{ visibility: squareInitLoading ? 'hidden' : 'visible' }}
                        />
                        <div className={s.addCardActions}>
                            <Button onClick={handleCancelAddCard}>Cancel</Button>
                            <Button
                                type="primary"
                                loading={saveCardLoading}
                                disabled={squareInitLoading || !!squareInitError}
                                onClick={handleSaveCard}
                            >
                                {saveCardLoading ? 'Verifying card...' : 'Save card'}
                            </Button>
                        </div>
                    </div>
                )}
                </>
                )}
            </div>

            <div className={s.modalFooter}>
                <Button className={s.cancelBtn} onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    className={s.chargeNowBtn}
                    disabled={!canCharge}
                    loading={chargeLoading}
                    onClick={handleChargeNow}
                >
                    Charge now
                </Button>
            </div>
        </Modal>
    );
};

export default ChargeCardModal;
